import React, { useEffect } from 'react';
import Navbar from './Navbar';
import CtaBanner from './CtaBanner';
import Footer from './Footer';
import { Check, PhoneCall } from 'lucide-react';
import './LifeInsurancePage.css';

const LifeInsurancePage = ({ onNavigate }) => {
  // Scroll to top and initialize scroll reveal observer
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    const animElements = document.querySelectorAll(
      '.insurance-detail-wrap, .insurance-detail-agent-wrap, .insurance-item'
    );
    animElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="life-insurance-page">
      {/* Top Navbar */}
      <Navbar isVisible={true} currentPage="services" onNavigate={onNavigate} />

      {/* Main Section: Insurance Detail + Agent Info Sidebar */}
      <section className="life-hero-section">
        <div className="life-container">
          <div className="grid-insurance-detail">
            
            {/* Left Column: Detail Content */}
            <div className="insurance-detail-wrap">
              <div className="insurance-detail-image-wrap">
                <img
                  src="https://cdn.prod.website-files.com/67163e2989a7a15aa5f26c4d/67188e722e929c96cc8985e7_insurance-01-detail.avif"
                  alt="Life insurance couple"
                  className="insurance-detail-image"
                  loading="eager"
                />
              </div>

              <h1 className="heading-h2">Life insurance</h1>
              <p className="text-lead">
                Ensure your loved ones are financially secure with tailored life insurance policies
              </p>
              <p className="insurance-detail-description">
                Knowing that your family is taken care of provides invaluable peace of mind, allowing you to focus on living your life with confidence. Whether you need temporary coverage or a lifelong policy, we offer flexible options that can adapt to your changing needs and circumstances.
              </p>

              <div className="w-richtext">
                <h6>Why life insurance matters</h6>
                <p>
                  Life is full of uncertainties, but securing your family’s future doesn’t have to be one of them. Life insurance provides a safety net for your loved ones, ensuring financial stability when they need it most. Whether it’s covering daily expenses, managing debts, or preparing for the unexpected, life insurance is an essential part of a well-rounded financial plan.
                </p>
                <p>
                  Life insurance guarantees that your loved ones receive the financial support they need to cover essential costs like mortgage payments, education, and day-to-day expenses.
                </p>

                <h6>Key benefits of insurance plans</h6>
                <p>
                  Our life insurance plans are designed to provide comprehensive protection and peace of mind. Here are the key benefits that make our plans stand out.
                </p>
                <ul className="life-benefits-list">
                  <li className="life-benefit-item">
                    <span className="life-benefit-check">
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <span>Ensure your loved ones are financially secure in case of an unexpected event.</span>
                  </li>
                  <li className="life-benefit-item">
                    <span className="life-benefit-check">
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <span>Whether you need term life insurance or lifelong protection, we have you covered.</span>
                  </li>
                  <li className="life-benefit-item">
                    <span className="life-benefit-check">
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <span>Access a portion of your death benefit early if you are diagnosed with a terminal illness.</span>
                  </li>
                  <li className="life-benefit-item">
                    <span className="life-benefit-check">
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <span>Tailor your life insurance plan to your unique needs with a range of coverage options and riders.</span>
                  </li>
                  <li className="life-benefit-item">
                    <span className="life-benefit-check">
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <span>Life insurance payouts are generally tax-free, allowing your beneficiaries to receive the full amount without worrying about extra tax burdens.</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right Column: Agent Information (Home Page Theme Colors) */}
            <div className="insurance-detail-agent-wrap">
              <h2 className="insurance-detail-agent-title">Agent information</h2>

              {/* Agent 1 */}
              <div className="agent-card-item">
                <div className="agent-header-row">
                  <img
                    src="https://cdn.prod.website-files.com/67163e2989a7a15aa5f26c4d/671875a801efabf42ffb803f_agent-02.avif"
                    alt="Bryan Knight"
                    className="insurance-detail-agent-image"
                    loading="eager"
                  />
                  <div className="agent-text-wrap">
                    <h3 className="insurance-detail-agent-name">Bryan Knight</h3>
                    <span className="agent-role">Claims Adjuster</span>
                  </div>
                </div>

                <div className="agent-info-list">
                  <div className="agent-info-list-item">
                    <img
                      src="https://cdn.prod.website-files.com/6715e3c1db68385479f9a22b/671879147769831e63a12ef2_icon-13.svg"
                      alt="Experience"
                      className="agent-info-list-icon"
                    />
                    <span><strong>10</strong> years of experience</span>
                  </div>
                  <div className="agent-info-list-item">
                    <img
                      src="https://cdn.prod.website-files.com/6715e3c1db68385479f9a22b/67187914765a13f0a84bcc6a_icon-14.svg"
                      alt="Awards"
                      className="agent-info-list-icon"
                    />
                    <span><strong>Many</strong> prestigious awards</span>
                  </div>
                </div>

                <button
                  className="button-primary-3"
                  onClick={() => onNavigate && onNavigate('contact')}
                >
                  <PhoneCall size={16} />
                  <span>Book a call</span>
                </button>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* Section 2: Other Insurances */}
      <section className="other-insurance-section">
        <div className="life-container">
          <div className="section-title-center">
            <h2>Other insurances</h2>
          </div>

          <div className="grid-insurance-split">
            {/* Home Insurance Card */}
            <div
              className="insurance-item"
              onClick={() => onNavigate && onNavigate('contact')}
            >
              <div className="insurance-image-wrap">
                <img
                  src="https://cdn.prod.website-files.com/67163e2989a7a15aa5f26c4d/67188ea6bd0139b5376fa2a0_insurance-02.avif"
                  alt="Home insurance"
                  className="insurance-image"
                  loading="lazy"
                />
              </div>
              <h3 className="insurance-title">Home insurance</h3>
              <p className="insurance-item-desc">
                Protect your personal property from unforeseen events like fire.
              </p>
              <ul className="insurance-feature-list">
                <li className="insurance-feature-list-item">
                  <img
                    src="https://cdn.prod.website-files.com/6715e3c1db68385479f9a22b/67160a037e1076a9344c3162_icon-04.svg"
                    alt="Check"
                    className="insurance-feature-list-icon"
                  />
                  <span>Liability for accidents</span>
                </li>
                <li className="insurance-feature-list-item">
                  <img
                    src="https://cdn.prod.website-files.com/6715e3c1db68385479f9a22b/67160a037e1076a9344c3162_icon-04.svg"
                    alt="Check"
                    className="insurance-feature-list-icon"
                  />
                  <span>Natural disaster options</span>
                </li>
                <li className="insurance-feature-list-item">
                  <img
                    src="https://cdn.prod.website-files.com/6715e3c1db68385479f9a22b/67160a037e1076a9344c3162_icon-04.svg"
                    alt="Check"
                    className="insurance-feature-list-icon"
                  />
                  <span>Security system discounts</span>
                </li>
              </ul>
            </div>

            {/* Auto Insurance Card */}
            <div
              className="insurance-item"
              onClick={() => onNavigate && onNavigate('contact')}
            >
              <div className="insurance-image-wrap">
                <img
                  src="https://cdn.prod.website-files.com/67163e2989a7a15aa5f26c4d/67188fce99b487ccb185d88c_insurance-03.avif"
                  alt="Auto insurance"
                  className="insurance-image"
                  loading="lazy"
                />
              </div>
              <h3 className="insurance-title">Auto insurance</h3>
              <p className="insurance-item-desc">
                Drive with confidence knowing that your vehicle is covered against accidents.
              </p>
              <ul className="insurance-feature-list">
                <li className="insurance-feature-list-item">
                  <img
                    src="https://cdn.prod.website-files.com/6715e3c1db68385479f9a22b/67160a037e1076a9344c3162_icon-04.svg"
                    alt="Check"
                    className="insurance-feature-list-icon"
                  />
                  <span>Comprehensive accident coverage</span>
                </li>
                <li className="insurance-feature-list-item">
                  <img
                    src="https://cdn.prod.website-files.com/6715e3c1db68385479f9a22b/67160a037e1076a9344c3162_icon-04.svg"
                    alt="Check"
                    className="insurance-feature-list-icon"
                  />
                  <span>24/7 roadside assistance</span>
                </li>
                <li className="insurance-feature-list-item">
                  <img
                    src="https://cdn.prod.website-files.com/6715e3c1db68385479f9a22b/67160a037e1076a9344c3162_icon-04.svg"
                    alt="Check"
                    className="insurance-feature-list-icon"
                  />
                  <span>Quick and easy claim settlements</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Home Page CTA Banner */}
      <CtaBanner onNavigate={onNavigate} />

      {/* Home Page Footer */}
      <Footer onNavigate={onNavigate} />
    </div>
  );
};

export default LifeInsurancePage;
